// src/utils/memberValidation.js
//
// Regras de cadastro de membro, em funções puras.
//
// Existe para que o formulário de ManagedAccountOnboarding.vue e a importação
// de planilha (utils/memberSheetImport.js) apliquem EXATAMENTE as mesmas
// checagens. Quando a validação da planilha era escrita à parte, ela aceitava
// linha que o formulário rejeitava — e o erro só aparecia no envio do lote,
// que é tudo-ou-nada.

// ─── Consentimento ───────────────────────────────────────────────────────────
// Menor de idade é responsabilidade do responsável legal; maior de idade, da
// instituição. O texto é gravado junto do vínculo (consent_text_snapshot), e
// por isso vale o que estava valendo no momento do cadastro.
export const CONSENT_FAMILY =
  "Declaro que sou pai, mãe ou responsável legal pelo usuário acima identificado e autorizo seu cadastro e uso da plataforma Litterae.";
export const CONSENT_EDU =
  "Declaro que possuo autorização adequada, no contexto educacional aplicável, para cadastrar o usuário acima identificado e permitir seu uso da plataforma Litterae, observadas as regras institucionais, contratuais e legais pertinentes.";

// Consentimento de quem ACEITA um convite (conta que já existia). É o que fica
// gravado no vínculo, com o IP e o navegador do aceite. CONSENT_EDU e
// CONSENT_FAMILY acima são a declaração de quem convida.
export function buildInviteAcceptConsent(circleName, relationshipType) {
  return (
    `Aceito participar de ${circleName} no Litterae, como ${relationshipType}. ` +
    `Entendo que ${circleName} e quem administra este círculo poderão ver meu nome, nome de usuário e e-mail ` +
    "e definir minhas permissões dentro dele, conforme os Termos de Uso e a Política de Privacidade."
  );
}

export const TERMS_VERSION = "v1.0";
export const PRIVACY_VERSION = "v1.0";

// Perfil de quem entra sem o campo preenchido. "Aluno" e não um rótulo neutro
// porque é o papel mais comum de quem é cadastrado em massa, e ele já tem
// molde sugerido pronto (ver utils/suggestedMemberProfiles.js) — quem entra
// assim nasce com uma grade coerente em vez de um tipo sem padrão nenhum.
export const DEFAULT_RELATIONSHIP_TYPE = "Aluno";

export const MIN_MEMBER_AGE = 4;
export const MIN_PASSWORD_LENGTH = 8;

// ─── Datas ───────────────────────────────────────────────────────────────────

export function getTodayLocalDateString() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function parseDateOnly(dateString) {
  if (!dateString) return null;
  const [year, month, day] = String(dateString).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

export function calculateAge(
  birthDateString,
  referenceDateString = getTodayLocalDateString()
) {
  const birth = parseDateOnly(birthDateString);
  const reference = parseDateOnly(referenceDateString);
  if (!birth || !reference) return null;
  let age = reference.getFullYear() - birth.getFullYear();
  const monthDiff = reference.getMonth() - birth.getMonth();
  const dayDiff = reference.getDate() - birth.getDate();
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) age--;
  return age;
}

export function calculateAgeFromIso(
  isoDate,
  referenceDateString = getTodayLocalDateString()
) {
  if (!isoDate) return null;
  const dateStr = String(isoDate).split("T")[0];
  return calculateAge(dateStr, referenceDateString);
}

// ─── Texto ───────────────────────────────────────────────────────────────────

// "filho" → "Filho"; "FILHO" → "FILHO" (mantém o resto das maiúsculas)
export function capitalizeFirst(value) {
  const v = (value ?? "").trim();
  if (!v) return v;
  return v.charAt(0).toUpperCase() + v.slice(1);
}

// ─── Campos ──────────────────────────────────────────────────────────────────

// O campo é type="email", mas o formulário não usa submit nativo — a validação
// do navegador nunca roda. Sem esta checagem, "fulano.silva" (sem domínio) era
// aceito e virava conta.
export function validateEmail(email) {
  const value = (email ?? "").trim();
  if (value === "") return "";
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)
    ? ""
    : "Informe um e-mail válido, com domínio (ex.: nome@dominio.com).";
}

export function passwordCriteria(password) {
  const p = password ?? "";
  return {
    length: p.length >= MIN_PASSWORD_LENGTH,
    letter: /[a-zA-Z]/.test(p),
    number: /\d/.test(p),
  };
}

export function isPasswordValid(password) {
  const c = passwordCriteria(password);
  return c.length && c.letter && c.number;
}

/**
 * Mensagem de erro da data de nascimento, ou "" quando está tudo certo.
 *
 * Data vazia devolve "" de propósito: quem exige o campo é o chamador (o
 * formulário pelo botão desabilitado, a importação pela checagem de
 * obrigatoriedade). Assim o campo em branco não fica vermelho antes de a
 * pessoa ter chance de preencher.
 */
export function validateBirthDate(
  birthDate,
  referenceDateString = getTodayLocalDateString()
) {
  if (!birthDate) return "";
  const birth = parseDateOnly(birthDate);
  const reference = parseDateOnly(referenceDateString);
  if (!birth || !reference) return "Informe uma data de nascimento válida.";
  if (birth > reference)
    return "A data de nascimento não pode ser posterior ao cadastro.";
  const age = calculateAge(birthDate, referenceDateString);
  if (age === null) return "Informe uma data de nascimento válida.";
  if (age < MIN_MEMBER_AGE)
    return `Só é permitido cadastrar membros com no mínimo ${MIN_MEMBER_AGE} anos de idade.`;
  return "";
}

export function isMinor(birthDate, referenceDateString = getTodayLocalDateString()) {
  if (!birthDate || validateBirthDate(birthDate, referenceDateString)) return false;
  const age = calculateAge(birthDate, referenceDateString);
  return age !== null && age < 18;
}

/**
 * Qual consentimento vale para esta data de nascimento. Data ausente ou
 * inválida cai no institucional — é o mesmo que o formulário já fazia, já que
 * o consentimento familiar depende de saber que a pessoa é menor.
 */
export function resolveConsentText(
  birthDate,
  referenceDateString = getTodayLocalDateString()
) {
  return isMinor(birthDate, referenceDateString) ? CONSENT_FAMILY : CONSENT_EDU;
}
